import React from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { getConnectionItemLabelText } from '../../../../../infrastructure/utils/connections.utils';
import { RoomPresentersConnectionsEntity } from '../../../../../modules/connections/room/room-presenters-connections.entity';
import './presenters-list-container.scss';
import { IConnectionDetailsDTO, IConnectionGroupDTO, IConnectionOption } from '../../../../../api/connections.dao';
import { MultiSelectButton } from '../../../../global/multi-select/multi-select-button';
import { ConnectionDropdownPresentation, unselectedOptionValue } from '../_connection-dropdown/connection-dropdown.presentation';
import { PresentersList } from './presenters-list';
import { addText, cancelText } from '../../../../../constants/common-labels.constants';

interface IProps {
    eventPresenters?: IAttendeeOrganizationDTO[];
    showConnectionsDropdown: boolean;
    vm: RoomPresentersConnectionsEntity;
    optionValue?: string;
    withHiddenBtn?: boolean;
    isInheritOptionExist?: boolean;
    isLabelVisibleCondition?: boolean;
    connections: IConnectionDetailsDTO[];
    connectionGroups?: IConnectionGroupDTO[];
    derivedConnection?: IConnectionDetailsDTO | null;
    assignedConnectionId?: number | null;
    getOptionItem?: (connectionId: number | null) => IConnectionOption;
    setPresenterRoleConnectionId?: (newConnectionId: number | null) => void;
}

const PresentersSection = mobxReact.observer((props: IProps) => {
    const {
        vm,
        connections,
        connectionGroups,
        eventPresenters,
        derivedConnection
    } = props;

    const assignedConnectionId = vm.hasAssignedConnection() ? vm.getAssignedConnection() : null;
    const assignedConnection = assignedConnectionId
        ? connections.concat(connectionGroups).find(({meetingConnectionId}) => meetingConnectionId === assignedConnectionId)
        : null
    ;

    const presentersOptionValue = (
        assignedConnection && getConnectionItemLabelText(assignedConnection) ||
        derivedConnection && getConnectionItemLabelText(derivedConnection) ||
        unselectedOptionValue
    );

    const isCancelledPresenterConnection = (assignedConnection || derivedConnection)?.cancelled ?? false;

    const onChange = mobx.action((connectionId: number | null): void => {
        if (connectionId === null) {
            vm.removeAssignedConnection();
        } else {
            vm.assignConnection(connectionId);
        }
    });

    const handlePresentersOnChange = (presenters): void => vm.setPresenters(presenters);

    return (
        <section className='presenters-section'>
            <div className='presenters-section__header'>
                <label className='presenters-section__header-label'>
                    <FormattedMessage
                        id='oe.common.presenters'
                        description='Label text'
                        defaultMessage='Presenters'
                    />
                </label>
                {
                    props.showConnectionsDropdown &&
                    <ConnectionDropdownPresentation
                        meetingConnections={connections}
                        derivedConnection={null}
                        assignedConnectionId={assignedConnectionId}
                        isLabelVisibleCondition={!!(connections.length && assignedConnectionId)}
                        onChange={onChange}
                        connectionGroups={connectionGroups}
                    />
                }
            </div>
            <PresentersList
                optionValue={presentersOptionValue}
                assignedPresenters={vm.getPresenters()}
                isCancelled={isCancelledPresenterConnection}
                isLabelVisibleCondition={presentersOptionValue !== unselectedOptionValue}
                removeAttendee={vm.removePresenterById} />
            {!props.withHiddenBtn && <MultiSelectButton
                items={eventPresenters}
                disabled={vm.getPresenters()}
                value={vm.getPresenters()}
                keyProperty='organizationId'
                nameProperty='name'
                text={
                    <FormattedMessage
                        id='oe.presenters.add'
                        description='Button text'
                        defaultMessage='{prefix} Add Presenters'
                        values={{
                            prefix: '+'
                        }}
                    />
                }
                applyButtonText={addText()}
                resetButtonText={cancelText()}
                onChange={handlePresentersOnChange}
            /> }
        </section>
    );
});

export { PresentersSection };
